<template>
  <div
    v-if="compositionMode !== CompositionMode.UNSUPPORTED && !isMpScope"
    class="mode-switcher"
  >
    <div :class="{ condensed }">
      <div :class="compositionModeConfig.bgClass">
        <Icon :icon="compositionModeConfig.icon" />
        <div class="switcher-content">
          <p class="heading">
            {{ $t(condensed ? compositionModeConfig.name : content.heading) }}
          </p>
          <p v-if="!condensed" class="text-sm">{{ $t(content.text) }}</p>
        </div>
        <div class="switcher-btn-wrapper">
          <button v-if="!condensed" class="btn btn-ghost" @click="switchMode">
            {{ $t(content.btnLabel) }}
          </button>
          <button v-else class="link icon-link icon-right" @click="switchMode">
            {{ $t('DESIGNS.LIST.TOOLBAR.TOGGLE_HEADER') }} <Icon icon="right" />
          </button>
        </div>

        <button
          v-if="!condensed"
          class="link link-underlined condense"
          @click="condense"
        >
          {{ $t('GENERAL.DISMISS') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CompositionMode } from '@spreadgroup/common-partner-lib/composition';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useCompositionStore } from 'commons/stores/composition';
import { usePublishingStore } from 'commons/stores/publishing';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import ProductSettingsModeSwitcherConfirmDialog from './ProductSettingsModeSwitcherConfirmDialog.vue';
import { compositionModeSettings } from 'commons/composition/compositionHelper';
import analytics from 'commons/tracking/analytics';

const store = useStore();
const publishingStore = usePublishingStore();

const compositionStore = useCompositionStore();
const compositionMode = computed(() => compositionStore.mode);
const isMpScope = computed(() => publishingStore.isMpScope);
const forceCondensed = ref<boolean>(false);

const hasIntentForCurrentScope = computed(
  () => store.getters['publishingCore/hasIntentForCurrentScope'],
);

const condensed = computed(
  () => forceCondensed.value || hasIntentForCurrentScope.value,
);

const compositionModeConfig = computed(
  () => compositionModeSettings[compositionMode.value],
);

const switchMode = () => {
  const targetMode =
    compositionMode.value === CompositionMode.SMART
      ? CompositionMode.ADVANCED
      : CompositionMode.SMART;
  dialogService
    .openDialog(ProductSettingsModeSwitcherConfirmDialog, {
      targetMode,
    })
    .then(async () => {
      await compositionStore.resetToMode(targetMode);
      analytics.logEvent('composition_mode_switch', {
        from: compositionMode.value,
        to: targetMode,
      });
    });
};

const condense = () => {
  forceCondensed.value = true;
};

const content = computed(() => {
  switch (compositionMode.value) {
    case CompositionMode.ADVANCED:
      return {
        heading:
          'PUBLISHING.DETAIL.PRODUCTS.MODES.SWITCH_ONBOARDING.HEADING_ADVANCED',
        text: 'PUBLISHING.DETAIL.PRODUCTS.MODES.SWITCH_ONBOARDING.TO_SMART',
        btnLabel: 'PUBLISHING.DETAIL.PRODUCTS.MODES.SWITCH_CONFIRM.TO_SMART',
      };
    default:
      return {
        heading:
          'PUBLISHING.DETAIL.PRODUCTS.MODES.SWITCH_ONBOARDING.HEADING_SMART',
        text: 'PUBLISHING.DETAIL.PRODUCTS.MODES.SWITCH_ONBOARDING.TO_ADVANCED',
        btnLabel: 'PUBLISHING.DETAIL.PRODUCTS.MODES.SWITCH_CONFIRM.TO_ADVANCED',
      };
  }
});
</script>

<style scoped lang="scss">
@import 'src/scss/constants';
@import 'src/scss/styleguide/colors';

.mode-switcher {
  margin-bottom: 24px;
}

.mode-switcher > div {
  display: flex;

  & > div {
    display: flex;
    padding: 12px;
    border-radius: $border-radius-medium;
    flex-grow: 1;
  }

  .icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    margin-right: 8px;
  }

  .switcher-content {
    flex-grow: 1;
    padding-right: 32px;
  }

  p {
    margin: 0;
  }

  .heading {
    font-weight: bold;
  }

  button {
    font-weight: normal;
    color: $pa-color-main-darker;
  }

  .switcher-btn-wrapper {
    display: flex;
    align-items: center;
  }

  .link {
    margin-left: 16px;
  }

  &.condensed > div {
    align-items: center;
    flex-grow: 0;

    .switcher-content {
      flex-grow: 0;
    }

    .link {
      margin-left: 0;
    }
  }
}
</style>
